import type { MaybeRef } from 'vue';

import { TinyEmitter } from 'tiny-emitter';
import { onBeforeUnmount, unref } from 'vue';

export interface TinyEmitterInstance {
  on: (event: string, callback: (...args: any[]) => void, ctx?: unknown) => TinyEmitterInstance;
  once: (event: string, callback: (...args: any[]) => void, ctx?: unknown) => TinyEmitterInstance;
  emit: (event: string, ...args: any[]) => TinyEmitterInstance;
  off: (event: string, callback?: (...args: any[]) => void) => TinyEmitterInstance;
}

export interface TinyEmitterConstructor {
  new (): TinyEmitterInstance;
}

const EmitterClass = TinyEmitter as unknown as TinyEmitterConstructor;

export interface EventBusHandlerRecord {
  emitter: TinyEmitterInstance;
  event: string;
  handler: (...args: any[]) => void;
}

export interface UseEventBusReturn {
  onEvent: <T = any>(event: string, handler: (payload: T) => void) => void;
  emitEvent: <T = any>(event: string, payload?: T) => void;
}

const eventEmitters = new Map<string, TinyEmitterInstance>();

function getEmitter(channelId?: MaybeRef<string | null | undefined>): TinyEmitterInstance {
  const key = (channelId ? unref(channelId) : null) || 'default';
  let emitter = eventEmitters.get(key);
  if (!emitter) {
    emitter = new EmitterClass();
    eventEmitters.set(key, emitter);
  }
  return emitter;
}

export default function useEventBus(
  channelId: MaybeRef<string | null | undefined> = 'default',
): UseEventBusReturn {
  const eventHandlers: EventBusHandlerRecord[] = [];

  onBeforeUnmount(() =>
    eventHandlers.forEach(({ emitter, event, handler }) =>
      emitter.off(event, handler),
    ),
  );

  return {
    onEvent: <T = any>(event: string, handler: (payload: T) => void) => {
      const emitter = getEmitter(channelId);
      eventHandlers.push({ emitter, event, handler });
      emitter.on(event, handler as (...args: any[]) => void);
    },
    emitEvent: <T = any>(event: string, payload?: T) => {
      const emitter = getEmitter(channelId);
      emitter.emit(event, payload);
    },
  };
}

export { useEventBus };
